import type { BankIdSessionResponse } from "@/store/slices/bankid.slice";
import type { BankIdData, DeliveryData } from "@/lib/types/checkout-steps";

export const STRIPE_3DS_SNAPSHOT_KEY = "refurbly_checkout_3ds_v1";

const SNAPSHOT_VERSION = 1 as const;
const MAX_AGE_MS = 60 * 60 * 1000;

export type Checkout3dsSnapshot = {
  v: typeof SNAPSHOT_VERSION;
  orderId: string;
  savedAt: number;
  verificationDetails: BankIdSessionResponse;
  bankIdContact: { email: string; phone: string };
  bankIdStepData: BankIdData;
  deliveryStepData: DeliveryData;
};

export function saveCheckout3dsSnapshot(
  data: Omit<Checkout3dsSnapshot, "v" | "savedAt">
) {
  const payload: Checkout3dsSnapshot = {
    v: SNAPSHOT_VERSION,
    savedAt: Date.now(),
    ...data,
  };
  try {
    sessionStorage.setItem(STRIPE_3DS_SNAPSHOT_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

export function readCheckout3dsSnapshot(
  orderId: string
): Checkout3dsSnapshot | null {
  try {
    const raw = sessionStorage.getItem(STRIPE_3DS_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Checkout3dsSnapshot;
    if (
      parsed?.v !== SNAPSHOT_VERSION ||
      parsed.orderId !== orderId ||
      typeof parsed.savedAt !== "number"
    ) {
      sessionStorage.removeItem(STRIPE_3DS_SNAPSHOT_KEY);
      return null;
    }
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(STRIPE_3DS_SNAPSHOT_KEY);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(STRIPE_3DS_SNAPSHOT_KEY);
    return null;
  }
}

export function clearCheckout3dsSnapshot() {
  try {
    sessionStorage.removeItem(STRIPE_3DS_SNAPSHOT_KEY);
  } catch {
    // ignore
  }
}

/** Remove Stripe 3DS return query params; preserve other params (e.g. utm). */
export function stripStripe3dsReturnParams() {
  const u = new URL(window.location.href);
  const keys = [
    "setup_intent",
    "setup_intent_client_secret",
    "redirect_status",
  ] as const;
  let changed = false;
  for (const k of keys) {
    if (u.searchParams.has(k)) {
      u.searchParams.delete(k);
      changed = true;
    }
  }
  if (changed) {
    const q = u.searchParams.toString();
    window.history.replaceState(
      {},
      "",
      u.pathname + (q ? `?${q}` : "") + u.hash
    );
  }
}
